import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
// import { AutofocusFixModule } from 'ngx-autofocus-fix';
import { AvatarModule } from 'ngx-avatars';

import { AppsModule } from '@apps/apps.module';
// import { ProcessModule } from '@apps/file-process/file-process.module';
// import { TaskWidgetComponent } from '@apps/fiduciary/cockpit/task-widget/task-widget.component';
import { UserTablesModule } from '@apps/user-tables/user-tables.module';
import { PlatformModule } from '@platform/platform.module';

import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DocumentLinkComponent } from '@apps/documents/cockpit/document-widget/document-link.component';
import { FiduciaryEffect } from '@apps/fiduciary/fiduciary.effects';
import { fiduciaryReducer } from '@apps/fiduciary/fiduciary.reducers';
import { FIDUCIARY_STATE_FEATURE_KEY } from '@apps/fiduciary/fiduciary.state';
import { TasksOverviewComponent } from '@apps/fiduciary/tasks/tasks-overview/tasks-overview.component';
import { TableFiltersContainerComponent } from '@apps/user-tables/table-filters-container/table-filters-container.component';
import { ShowAuthorizeDirective } from '@platform/auth/show-authorize.directive';
import { AutofocusDirectiveModule } from '@platform/autofocus/autofocus.module';
import { BackButtonComponent } from '@platform/components/back-button/back-button.component';
import { ButtonAddExistingUserComponent } from '@platform/components/button-add-existing-user.component';
import { ButtonAddComponent } from '@platform/components/buttons/button-add/button-add.component';
import { ButtonCancelComponent } from '@platform/components/buttons/button-cancel.component';
import { ButtonCloseComponent } from '@platform/components/buttons/button-close.component';
import { ButtonIconAddComponent } from '@platform/components/buttons/button-icon-add.component';
import { ButtonIconUploadComponent } from '@platform/components/buttons/button-icon-upload.component';
import { ButtonSaveComponent } from '@platform/components/buttons/button-save.component';
import { LinkButtonComponent } from '@platform/components/buttons/link-button/link-button.component';
import { LoadingButtonDirective } from '@platform/components/buttons/loading-button/loading-button.directive';
import { EmptyListComponent } from '@platform/components/empty-list/empty-list.component';
import { FormAutocompleteComponent } from '@platform/components/forms/form-autocomplete/form-autocomplete.component';
import { FormButtonToggleComponent } from '@platform/components/forms/form-button-toggle/form-button-toggle.component';
import { FormDatepickerComponent } from '@platform/components/forms/form-datepicker/form-datepicker.component';
import { FormInputComponent } from '@platform/components/forms/form-input/form-input.component';
import { FormSelectComponent } from '@platform/components/forms/form-select/form-select.component';
import { FormToggleComponent } from '@platform/components/forms/form-toggle/form-toggle.component';
import { LoadingSpinnerOverlayComponent } from '@platform/components/loading-spinner-overlay/loading-spinner-overlay.component';
import { LoadingSpinnerComponent } from '@platform/components/loading-spinner/loading-spinner.component';
import { PageHeaderComponent } from '@platform/components/page-header/page-header.component';
import { PreComponent } from '@platform/components/pre/pre.component';
import { SearchInputComponent } from '@platform/components/search-input/search-input.component';
import { UserIsExternalDropdownComponent } from '@platform/components/user-is-external-dropdown/user-is-external-dropdown.component';
import { ShortenPipe } from '@platform/shared/shorten.pipe';
import { QRCodeComponent } from 'angularx-qrcode';
import { ScanDocumentButtonComponent } from '../apps/fiduciary/cockpit/inbox-upload-history/scan-document-button/scan-document-button.component';
import { FormInputReadonlyComponent } from '../platform/components/forms/form-input-readonly/form-input-readonly.component';
import { AdminComponent } from './admin/admin.component';
import { CallbackComponent } from './callback/callback.component';
import { CockpitTitleComponent } from './cockpit/cockpit-title.component';
import { CockpitComponent } from './cockpit/cockpit.component';
import { CreditorWidgetComponent } from './cockpit/creditor-widget/creditor-widget.component';
import { DocumentWidgetTableComponent } from './cockpit/document-widget/document-widget-table.component';
import { DocumentWidgetComponent } from './cockpit/document-widget/document-widget.component';
import { WidgetChipComponent } from './cockpit/document-widget/widget-chip.component';
import { EmptyPlaceholderComponent } from './cockpit/empty-placeholder.component';
import { PayrollWidgetComponent } from './cockpit/payroll-widget/payroll-widget.component';
import { DisplayLegalEntitiesWithoutProcessesComponent } from './cockpit/process-widget/display-legal-entities-without-processes/display-legal-entities-without-processes.component';
import { ProcessWidgetTableCellComponent } from './cockpit/process-widget/process-widget-table-cell.component';
import { ProcessWidgetComponent } from './cockpit/process-widget/process-widget.component';
import { ProcessesWidgetTableComponent } from './cockpit/process-widget/processes-widget-table.component';
import { ProjectNameDisplayComponent } from './cockpit/project-widget/project-widget-table/project-name-display.component';
import { ProjectNumberDisplayComponent } from './cockpit/project-widget/project-widget-table/project-number-display.component';
import { ProjectWidgetTableComponent } from './cockpit/project-widget/project-widget-table/project-widget-table.component';
import { ProjectWidgetComponent } from './cockpit/project-widget/project-widget.component';
import { TableColumnComponent } from './cockpit/project-widget/table-column/table-column.component';
import { TaskWidgetTableComponent } from './cockpit/task-widget/task-widget-table.component';
import { TaskWidgetComponent } from './cockpit/task-widget/task-widget.component';
import { WidgetFileUploadComponent } from './cockpit/widget-file-upload/widget-file-upload.component';
import { WidgetSelectorSpinnerComponent } from './cockpit/widget/widget-spinner/widget-selector-spinner.component';
import { WidgetSpinnerComponent } from './cockpit/widget/widget-spinner/widget-spinner.component';
import { WidgetComponent } from './cockpit/widget/widget.component';
import { ContactsComponent } from './contacts/contacts.component';
import { CoreRoutingModule } from './core-routing.module';
import { CoreEffects } from './core.effects';
import { coreReducer } from './core.reducers';
import { CORE_STATE_FEATURE_KEY } from './core.state';
import { CreateUserComponent } from './create-user/create-user.component';
import { FormToggleFilterUsersByCustomerSpaceComponent } from './create-user/form-toggle-filter-users-by-customer-space/form-toggle-filter-users-by-customer-space.component';
import { FormToggleUserLanguageComponent } from './create-user/form-toggle-user-language/form-toggle-user-language.component';
import { FormToggleUserStatusComponent } from './create-user/form-toggle-user-status/form-toggle-user-status.component';
import { CustomerSpaceAdminV2Component } from './customer-space-admin-v2/customer-space-admin-v2.component';
import { CreateUserFormComponent } from './customer-space-admin/create-user-form/create-user-form.component';
import { CustomerSpaceAdminComponent } from './customer-space-admin/customer-space-admin.component';
import { CustomerSpaceUsersTableComponent } from './customer-space-admin/feature/customer-space-users-table.component';
import { DisplayApplicationsComponent } from './customer-space-admin/ui/display-applications.component';
import { CustomerSpaceListComponent } from './customer-space-list/customer-space-list.component';
import { EditUserComponent } from './edit-user/edit-user.component';
import { FatalErrorComponent } from './fatal-error/fatal-error.component';
import { HomeShellComponent } from './home/home-shell/home-shell.component';
import { LogoutComponent } from './logout/logout.component';
import { MobileUploadExpiredComponent } from './mobile-upload/mobile-upload-expired/mobile-upload-expired.component';
import { MobileUploadFilePreviewComponent } from './mobile-upload/mobile-upload-file-preview/mobile-upload-file-preview.component';
import { MobileUploadQrDialogComponent } from './mobile-upload/mobile-upload-qr-dialog/mobile-upload-qr-dialog.component';
import { MobileUploadComponent } from './mobile-upload/mobile-upload.component';
import { NavigationErrorComponent } from './navigation-error/navigation-error.component';
import { NotFoundComponent } from './notfound/notfound.component';
import { NotificationDrawerComponent } from './notification/notification-drawer.component';
import { FooterComponent } from './shell/footer/footer.component';
import { CompanyLogoComponent } from './shell/header/company-logo.component';
import { CustomerSpaceAdministrationLinkComponent } from './shell/header/customer-space-administration-link.component';
import { HeaderComponent } from './shell/header/header.component';
import { HomeLinkComponent } from './shell/header/home-link.component';
import { NotificationButtonComponent } from './shell/header/notification-button.component';
import { OverviewLinkComponent } from './shell/header/overview-link.component';
import { PortalAdministrationLinkComponent } from './shell/header/portal-administration-link.component';
import { PlainShellComponent } from './shell/plain-shell.component';
import { ShellComponent } from './shell/shell.component';
import { UnauthenticatedComponent } from './unauthenticated/unauthenticated.component';
import { UnauthorizedForExternalComponent } from './unauthorized-for-external/unauthorized-for-external.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { UpdateEmployeeDialogComponent } from './update-employee-dialog/update-employee-dialog.component';
import { UsersManagementTableComponent } from './user-list/feature/users-management-table/users-management-table.component';
import { UserListComponent } from './user-list/user-list.component';
import { UserManagementTableComponent } from './user-management/user-management-table/user-management-table.component';
import { UserManagementComponent } from './user-management/user-management.component';
import { ProfilePictureDialogComponent } from './user-profile/profile-picture-dialog/profile-picture-dialog.component';
import { TitlePersonalInformationComponent } from './user-profile/title-personal-information.component';
import { TitleSettingsComponent } from './user-profile/title-settings.component';
import { UserAuthenticationEditorComponent } from './user-profile/user-authentication-editor/user-authentication-editor.component';
import { UserDeputiesEditorComponent } from './user-profile/user-deputies-editor/user-deputies-editor.component';
import { UserPictureEditorComponent } from './user-profile/user-picture-editor/user-picture-editor.component';
import { UserProfileComponent } from './user-profile/user-profile.component';

@NgModule({
  declarations: [
    CustomerSpaceAdminComponent,
    CallbackComponent,
    // TaskWidgetComponent,
    UnauthorizedComponent,
    UnauthorizedForExternalComponent,
    FatalErrorComponent,
    UpdateEmployeeDialogComponent,
    CustomerSpaceListComponent,
    UserListComponent,
    NavigationErrorComponent,
    UnauthenticatedComponent,
    LogoutComponent,
    NotFoundComponent,
    ContactsComponent,
    CustomerSpaceUsersTableComponent,
    DisplayApplicationsComponent,
    UsersManagementTableComponent,
    MobileUploadComponent,
    MobileUploadQrDialogComponent,
    MobileUploadExpiredComponent,
    MobileUploadFilePreviewComponent,
    ShellComponent,
    FooterComponent,
    CockpitComponent,
    WidgetComponent,
    TaskWidgetComponent,
    TaskWidgetTableComponent,
    WidgetSelectorSpinnerComponent,
    DocumentWidgetComponent,
    DocumentWidgetTableComponent,
    CreditorWidgetComponent,
    PayrollWidgetComponent,
    ProjectWidgetComponent,
    ProjectWidgetTableComponent,
    WidgetFileUploadComponent,
    ProcessWidgetComponent,
    ProcessesWidgetTableComponent,
    DisplayLegalEntitiesWithoutProcessesComponent,
    ProcessWidgetTableCellComponent,
    CreateUserComponent,
    NotificationDrawerComponent,
    HomeShellComponent,
    PlainShellComponent,
    UserProfileComponent,
    UserDeputiesEditorComponent,
    EditUserComponent,
    UserAuthenticationEditorComponent,
    UserPictureEditorComponent,
    UserManagementComponent,
    UserManagementTableComponent,
    CreateUserFormComponent,
    CustomerSpaceAdminV2Component,
  ],
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    MatBadgeModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatDividerModule,
    MatTableModule,
    MatSortModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatChipsModule,
    FormsModule,
    MatMenuModule,
    MatToolbarModule,
    MatSlideToggleModule,
    AvatarModule,
    MatPaginatorModule,
    // AutofocusFixModule.forRoot(),
    StoreModule.forFeature(CORE_STATE_FEATURE_KEY, coreReducer),
    EffectsModule.forFeature([CoreEffects]),
    StoreModule.forFeature(FIDUCIARY_STATE_FEATURE_KEY, fiduciaryReducer),
    EffectsModule.forFeature([FiduciaryEffect]),
    CoreRoutingModule,
    PlatformModule,
    AppsModule,
    // ProcessModule,
    UserTablesModule,
    HeaderComponent,
    QRCodeComponent,
    OverviewLinkComponent,
    HomeLinkComponent,
    NotificationButtonComponent,
    PortalAdministrationLinkComponent,
    ShortenPipe,
    CustomerSpaceAdministrationLinkComponent,
    CompanyLogoComponent,
    ButtonCloseComponent,
    LinkButtonComponent,
    ButtonAddComponent,
    AutofocusDirectiveModule,
    WidgetSpinnerComponent,
    ButtonIconAddComponent,
    ButtonIconUploadComponent,
    WidgetChipComponent,
    DocumentLinkComponent,
    CockpitTitleComponent,
    ProjectNameDisplayComponent,
    ProjectNumberDisplayComponent,
    EmptyPlaceholderComponent,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    PreComponent,
    FormInputComponent,
    FormAutocompleteComponent,
    FormToggleUserLanguageComponent,
    FormDatepickerComponent,
    TableColumnComponent,
    FormInputReadonlyComponent,
    FormToggleComponent,
    FormButtonToggleComponent,
    FormSelectComponent,
    FormToggleUserStatusComponent,
    ButtonCancelComponent,
    ButtonSaveComponent,
    BackButtonComponent,
    TitleSettingsComponent,
    TitlePersonalInformationComponent,
    TableFiltersContainerComponent,
    FormToggleFilterUsersByCustomerSpaceComponent,
    ScanDocumentButtonComponent,
    EmptyListComponent,
    ShowAuthorizeDirective,
    TasksOverviewComponent,
    PageHeaderComponent,
    ProfilePictureDialogComponent,
    LoadingSpinnerComponent,
    LoadingSpinnerOverlayComponent,
    LoadingButtonDirective,
    ButtonAddExistingUserComponent,
    UserIsExternalDropdownComponent,
    SearchInputComponent,
    AdminComponent,
  ],
})
export class CoreModule {}
